import { Phone, Mail, MapPin, Clock, Instagram, Building2 } from "lucide-react";
import { WA_URL, WhatsAppIcon } from "@/components/WhatsAppButton";
import Seo from "@/components/Seo";
import { PageHero } from "@/pages/About";
import { Reveal } from "@/components/Reveal";
import { ContactForm } from "@/components/LeadForms";
import { useLanguage } from "@/context/LanguageContext";
import { useSiteContent } from "@/context/SiteContext";

const DEFAULT_PHONE = "(786) 774-6692";
const DEFAULT_EMAIL = "Carlos.vindel@masterteamrealty.com";

export default function Contact() {
  const { lang, t } = useLanguage();
  const { content } = useSiteContent();
  const PHONE = content?.phone || DEFAULT_PHONE;
  const EMAIL = content?.email || DEFAULT_EMAIL;
  const HOURS = content?.hours?.[lang] || t.contactPage.hours;
  return (
    <main data-testid="contact-page">
      <Seo
        title="Contact Carlos Vindel — Miami Real Estate Agent"
        siteName="Carlos Vindel Realty"
        description="Ready to buy, sell, or value your South Florida home? Contact Carlos Vindel for a free, no-obligation conversation. Se habla español."
      />
      <PageHero testid="contact-hero" overline={t.contactPage.overline} title={t.contactPage.title} subtitle={t.contactPage.subtitle} />

      <section className="bg-cream py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-5 gap-14">
          <Reveal className="lg:col-span-2">
            <div className="bg-ink grain-overlay relative p-8 sm:p-10 text-cream lg:sticky lg:top-28">
              <img
                src="/images/carlos-headshot.jpg"
                alt="Carlos Vindel"
                loading="lazy"
                className="w-24 h-24 rounded-full object-cover object-top border-2 border-gold"
              />
              <p className="mt-5 font-serif text-2xl">{t.contactPage.directTitle}</p>
              <div className="mt-4 inline-flex items-center gap-3 bg-white px-4 py-2.5">
                <img src="/images/realtor-logo.png" alt="REALTOR®" data-testid="contact-realtor-logo" className="h-7 w-auto" />
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-ink/60">FL #3654913</span>
              </div>
              <ul className="mt-8 space-y-5 text-sm">
                <li>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-1">{t.contactPage.phoneLabel}</p>
                  <a data-testid="contact-phone-link" href={`tel:+1${PHONE.replace(/\D/g, "")}`} className="flex items-center gap-3 text-cream/85 hover:text-gold transition-colors">
                    <Phone className="w-4 h-4 text-gold" /> {PHONE}
                  </a>
                </li>
                <li>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-1">{t.contactPage.emailLabel}</p>
                  <a data-testid="contact-email-link" href={`mailto:${EMAIL}`} className="flex items-center gap-3 text-cream/85 hover:text-gold transition-colors">
                    <Mail className="w-4 h-4 text-gold" /> {EMAIL}
                  </a>
                </li>
                <li>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-1">{t.contactPage.locationLabel}</p>
                  <p className="flex items-center gap-3 text-cream/85">
                    <MapPin className="w-4 h-4 text-gold" /> {t.contactPage.location}
                  </p>
                </li>
                <li>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-1">{t.contactPage.brokerageLabel}</p>
                  <a data-testid="contact-brokerage-link" href="https://masterteamrealty.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-cream/85 hover:text-gold transition-colors">
                    <Building2 className="w-4 h-4 text-gold" /> Master Team Realty
                  </a>
                </li>
                <li>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-1">{t.contactPage.hoursLabel}</p>
                  <p className="flex items-center gap-3 text-cream/85">
                    <Clock className="w-4 h-4 text-gold" /> {HOURS}
                  </p>
                </li>
                <li>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-1">WhatsApp</p>
                  <a data-testid="contact-whatsapp-link" href={WA_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-cream/85 hover:text-gold transition-colors">
                    <WhatsAppIcon className="w-4 h-4 text-gold" /> {PHONE}
                  </a>
                </li>
              </ul>
              <div className="mt-9 pt-7 border-t border-white/10">
                <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-4">{t.contactPage.followLabel}</p>
                <div className="flex items-center gap-3">
                  <a
                    data-testid="contact-social-instagram"
                    href="https://www.instagram.com/cvmiamirealtor"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"
                  >
                    <Instagram className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-3 bg-white border border-sand/80 p-7 sm:p-10 shadow-[0_8px_40px_rgba(13,19,26,0.06)]">
            <ContactForm />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
